import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { finalize, Subscription } from 'rxjs';
import {
  Factory,
  FactoryBatchStock,
  ProductStockAdjustmentContext,
  FactoryStockItem,
} from '../../production-inventory.model';
import { ProductionInventoryService } from '../../services/production-inventory.service';
import { PaginatedResponse } from '../../../common/models/common.model';
import { DatePipe } from '@angular/common';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { toNumberOrNull } from '../../../utils';

interface BatchRow {
  /** The row this was flattened from. The adjustment needs its ids, not just its labels. */
  source: FactoryBatchStock;
  batch_number: string;
  mfg_date: string;
  exp_date: string;
  boxes_available: string;
  units_available: string;
  factory_name: string;
  packaging: string;
  total_stock_cost: string;
}

interface GroupedBatchStock {
  product_name: string;
  rows: BatchRow[];
  total_cost: number;
}

@Component({
  selector: 'app-factory-stock',
  standalone: false,
  templateUrl: './factory-stock.component.html',
  styleUrl: './factory-stock.component.scss',
  providers: [DatePipe],
})
export class FactoryStockComponent implements OnInit, OnDestroy {
  @Input() factoryList: Factory[] = [];
  @ViewChild('exportModal') exportModalRef!: TemplateRef<any>;

  protected readonly Math = Math;

  protected stockItems: FactoryStockItem[] = [];
  protected batchStockItems: FactoryBatchStock[] = [];
  protected groupedBatchStock: GroupedBatchStock[] = [];
  protected isDataLoading = false;
  protected selectedFactory: string | null = null;
  protected showBatchView = false;

  protected totalCount = 0;
  protected hasNextPage = false;
  protected hasPreviousPage = false;
  protected currentPage = 1;
  protected totalStockValue: number | null = null;

  protected exportFactory: string | null = null;
  protected isExporting: boolean = false;
  protected exportError: any = null;

  protected adjustContext: ProductStockAdjustmentContext | null = null;
  protected historyBatch: FactoryBatchStock | null = null;
  protected historyRefreshToken = 0;

  private adjustModalRef: NgbModalRef | null = null;
  private subscription: Subscription = new Subscription();

  constructor(
    private productionInventoryService: ProductionInventoryService,
    private datePipe: DatePipe,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.loadStock();
  }

  protected onFactoryChange(): void {
    this.currentPage = 1;
    this.loadStock();
  }

  protected toggleBatchView(): void {
    this.showBatchView = !this.showBatchView;
    this.currentPage = 1;
    this.loadStock();
  }

  private loadStock(): void {
    this.isDataLoading = true;

    const params: any = { page: this.currentPage };
    if (this.selectedFactory) {
      params['factory'] = this.selectedFactory;
    }

    if (this.showBatchView) {
      this.subscription.add(
        this.productionInventoryService
          .getFactoryBatchStocks(params)
          .pipe(finalize(() => (this.isDataLoading = false)))
          .subscribe({
            next: (response: PaginatedResponse<FactoryBatchStock>) => {
              this.batchStockItems = response.results || [];
              this.groupedBatchStock = this.groupBatchStock(this.batchStockItems);
              this.totalCount = response.count;
              this.hasNextPage = !!response.next;
              this.hasPreviousPage = !!response.previous;
              this.totalStockValue = response.total_stock_value ?? null;
            },
          })
      );
    } else {
      this.subscription.add(
        this.productionInventoryService
          .getFactoryStocks(params)
          .pipe(finalize(() => (this.isDataLoading = false)))
          .subscribe({
            next: (response: PaginatedResponse<FactoryStockItem>) => {
              this.stockItems = response.results || [];
              this.totalCount = response.count;
              this.hasNextPage = !!response.next;
              this.hasPreviousPage = !!response.previous;
              this.totalStockValue = response.total_stock_value ?? null;
            },
          })
      );
    }
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.loadStock();
  }

  private groupBatchStock(items: FactoryBatchStock[]): GroupedBatchStock[] {
    const grouped = new Map<string, BatchRow[]>();

    for (const item of items) {
      const row: BatchRow = {
        source: item,
        batch_number: item.batch_number || '-',
        mfg_date: item.manufacturing_date
          ? this.datePipe.transform(item.manufacturing_date, 'dd MMM yyyy') || '-'
          : '-',
        exp_date: item.expiry_date ? this.datePipe.transform(item.expiry_date, 'dd MMM yyyy') || '-' : '-',
        boxes_available: item.boxes_available,
        units_available: item.units_available,
        factory_name: item.factory_name,
        packaging: `${item.packing_size} ${item.unit}${item.packaging_type ? ' / ' + item.packaging_type : ''}`,
        total_stock_cost: item.total_stock_cost,
      };

      if (!grouped.has(item.product_name)) {
        grouped.set(item.product_name, []);
      }
      grouped.get(item.product_name)!.push(row);
    }

    return Array.from(grouped.entries()).map(([product_name, rows]) => ({
      product_name,
      rows,
      total_cost: rows.reduce((sum, r) => sum + (parseFloat(r.total_stock_cost) || 0), 0),
    }));
  }

  /**
   * The confirmation needs the product's aggregate at this factory as well as the batch
   * balance, and the batch list does not carry it, so it is fetched when the form opens.
   * The adjustment still posts without it: the aggregate is only used to state the second
   * balance the admin is about to move.
   */
  protected openAdjustModal(batch: FactoryBatchStock, content: TemplateRef<any>): void {
    this.adjustContext = this.buildAdjustContext(batch, null);
    this.adjustModalRef = this.modalService.open(content, { size: 'lg', backdrop: 'static' });

    this.subscription.add(
      this.productionInventoryService.getFactoryStocks({ factory: batch.factory, page_size: 100 }).subscribe({
        next: (response: PaginatedResponse<FactoryStockItem>) => {
          const aggregate = (response.results || []).find(item => item.product_packaging === batch.product_packaging);
          if (aggregate && this.adjustContext?.production_entry === batch.production_entry) {
            this.adjustContext = this.buildAdjustContext(batch, aggregate.units_available);
          }
        },
        // The aggregate only enriches the confirmation; the adjustment posts without it.
        error: () => undefined,
      })
    );
  }

  private buildAdjustContext(batch: FactoryBatchStock, factoryUnits: string | null): ProductStockAdjustmentContext {
    return {
      scope: 'factory',
      production_entry: batch.production_entry,
      batch_number: batch.batch_number,
      product_name: batch.product_name,
      packaging_label: `${batch.packing_size} ${batch.unit}${batch.packaging_type ? ' / ' + batch.packaging_type : ''}`,
      container_id: batch.factory,
      container_name: batch.factory_name,
      nos: toNumberOrNull(batch.nos) ?? 0,
      unit: batch.unit,
      batch_units_available: batch.units_available,
      container_units_available: factoryUnits,
    };
  }

  protected onAdjustmentSaved(): void {
    this.adjustModalRef?.close();
    this.adjustModalRef = null;
    this.adjustContext = null;

    // Both the batch balance and the product aggregate moved, and this screen reads both.
    this.loadStock();
    this.historyRefreshToken += 1;
  }

  protected onAdjustCancelled(): void {
    this.adjustModalRef?.dismiss();
    this.adjustModalRef = null;
    this.adjustContext = null;
  }

  protected openHistoryModal(batch: FactoryBatchStock, content: TemplateRef<any>): void {
    this.historyBatch = batch;
    this.modalService.open(content, { size: 'xl' });
  }

  protected openExportModal(): void {
    this.exportFactory = null;
    this.exportError = null;
    this.modalService.open(this.exportModalRef, { size: 'md' });
  }

  protected onExportSubmit(modalRef: any): void {
    this.isExporting = true;
    this.exportError = null;

    const q: Record<string, any> = {};
    if (this.exportFactory) q['factory'] = this.exportFactory;

    this.productionInventoryService
      .export_factory_batch_stock(q)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'factory_stock.xlsx';
          a.click();
          URL.revokeObjectURL(url);
          modalRef.close();
        },
        error: err => {
          this.exportError = err;
        },
      });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.adjustModalRef?.dismiss();
  }
}
